import { message as antdMessage, Button, Form, Input, Select, Checkbox } from 'antd';
import { useRouter } from 'next/router';
import Link from 'next/link';
import { useEffect, useRef, useState } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';

import { ENGLISH_LANG, rpcMap, i18nMap } from '@/constants';

import { INIT_COUNTRY_MAP } from '@/constants/quote';
import { useLang } from '@/i18n';
import { getKaamelPlan } from '@/utils/kaamelTest';
import { createOceanProLead, getCitiesList, getCountriesList, getStatesList } from '@/service';
import gtm from '@/utils/gtm';
import EcoSectionCommon from 'eco-design/elements/eco-section-common';
import styles from './index.module.scss';

const SECRET_KEY = process.env.NEXT_PUBLIC_RECAPTCHA_REGISTER_SECRET_KEY;

antdMessage.config({ top: 200 });

interface StoreProps {
    plan: 'a' | 'b' | 'c';
    expired: number;
    privacyUrl: string;
    pluginUrl: string;
    isStatic?: string;
}

export default function Comp({ onSuccess }: any) {
    const { t } = useLang('theProduct.PartnerForm');
    const { t: t2 } = useLang('shared');
    const { locale } = useRouter();
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const recaptchaRef = useRef();
    const [recaptchaResponse, setRecaptchaResponse] = useState('');
    const hl = rpcMap[(ENGLISH_LANG?.includes(locale) ? 'en' : locale) as string];
    const [countries, setCountries] = useState<any>([]);
    const [states, setStates] = useState<{ key: number; label: string; value: string }[]>([]);
    const [cities, setCities] = useState<{ key: number; label: string; value: string }[]>([]);
    const [prefixCode, setPrefixCode] = useState('');
    const [isAgreed, setIsAgreed] = useState(true);

    const waitTimer = useRef<any>(null);

    const urlParams = new URLSearchParams(window.location.search);
    const [kaamelInfo, setKaamelInfo] = useState<StoreProps | undefined>();

    const onFinish = async (values: any) => {
        const utmParams: any = {};

        const utmSource = urlParams.get('utm_source');
        const utmMedium = urlParams.get('utm_medium');
        const utmCampaign = urlParams.get('utm_campaign');

        if (!!utmSource) {
            utmParams.utmSource = utmSource;
        }

        if (!!utmMedium) {
            utmParams.utmMedium = utmMedium;
        }

        if (!!utmCampaign) {
            utmParams.utmCampaign = utmCampaign;
        }
        const params: any = {
            ...values,
            email: values.email.trim(),
            recaptchaResponse,
            phone: `${prefixCode} ${values?.phone}`,
            acceptsMarketing: isAgreed,
            source: 'brandsite_ocean_pro',
            ...utmParams,
        };

        setLoading(true);
        try {
            const res = await createOceanProLead(params);
            setLoading(false);
            if (res.code === '0') {
                form.resetFields();
                handleInitCountry();
                antdMessage.success(res?.message || 'Success');
                onSuccess && onSuccess();
                gtm.push({
                    event: 'uaEvent',
                    event_name: 'pre_order_submit',
                    button_name: 'EcoFlow Ocean Pro Be the First to Know_Submit',
                    status: 'success',
                    __btn_id1: styles.comp,
                    __btn_id2: t('submit_btn'),
                });
            } else {
                setRecaptchaResponse('');
                const rptRef: any = recaptchaRef.current;
                rptRef.reset();
                antdMessage.error(res?.message);
                gtm.push({
                    event: 'uaEvent',
                    event_name: 'pre_order_submit',
                    button_name: 'EcoFlow Ocean Pro Be the First to Know_Submit',
                    status: 'fail',
                    __btn_id1: styles.comp,
                    __btn_id2: t('submit_btn'),
                });
            }
        } catch (err: any) {
            setLoading(false);
            setRecaptchaResponse('');
            const rptRef: any = recaptchaRef.current;
            rptRef.reset();
            console.log('err:', err);
            gtm.push({
                event: 'uaEvent',
                event_name: 'pre_order_submit',
                button_name: 'EcoFlow Ocean Pro Be the First to Know_Submit',
                status: 'fail',
            });
        }
    };

    const getCountry = async () => {
        // 因国家/省/市列表只有英文的，所以暂时写死语言为 us
        const result: any = await getCountriesList(
            {
                page: 1,
                size: 300,
            },
            'us',
        );
        if (result?.code === '0') {
            const countryList: any = [];
            result.data?.content?.forEach((item: any) => {
                countryList.push({
                    key: item.id,
                    text: item.name,
                    phoneCode: item.phoneCode,
                });
            });
            countryList.push({
                key: 404,
                text: t('other_country'),
                phoneCode: '',
            });
            setCountries(countryList);
        }
    };

    const getStates = async (id: number) => {
        try {
            const res: any = await getStatesList({
                countryId: id,
                page: 1,
                size: 300,
            });
            if (res.code === '0') {
                const stateList = res.data?.content?.map((item: any) => ({
                    key: item.id,
                    label: item.name,
                    value: item.name,
                }));
                setStates(stateList);
                return stateList;
            } else {
                antdMessage.error(res.message);
            }
        } catch (err) {
            console.log(err);
        }
    };

    const getCity = async (id: number) => {
        try {
            const res: any = await getCitiesList({
                stateId: id,
                page: 1,
                size: 3000,
            });
            if (res.code === '0') {
                const cityList = res.data?.content?.map((item: any) => ({
                    key: item.id,
                    label: item.name,
                    value: item.name,
                }));
                setCities(cityList);
            } else {
                antdMessage.error(res.message);
            }
        } catch (err) {
            console.log(err);
        }
    };

    const onReCAPTCHAChange = async (captchaCode: string) => {
        if (!captchaCode) return;
        setRecaptchaResponse(captchaCode);
        form.setFields([
            {
                name: 'recaptchaResponse',
                value: captchaCode,
                errors: [],
            },
        ]);
    };

    const handleInitCountry = () => {
        form.setFieldsValue({ countryName: INIT_COUNTRY_MAP[locale as string]?.name || 'Germany' });
        setPrefixCode('+1');
        getStates(INIT_COUNTRY_MAP[locale as string]?.id || 82);
    };

    const onCountryChange = (_val: any, option: any) => {
        form.setFieldsValue({
            state: undefined,
        });
        form.setFieldsValue({
            county: undefined,
        });

        const target = countries.filter((item: any) => item.key === Number(option.key));
        setPrefixCode(target[0]?.phoneCode);
        getStates(option.key);
    };

    const onStateChange = (_val: any, option: any) => {
        form.setFieldsValue({
            county: undefined,
        });
        getCity(option.key);
    };
    useEffect(() => {
        getCountry();

        waitTimer.current = setTimeout(() => {
            handleInitCountry();
        }, 300);
    }, []);

    useEffect(() => {
        const info = getKaamelPlan();
        setKaamelInfo(info);
    }, []);

    return (
        <div className={styles.comp}>
            <EcoSectionCommon sectionCommonLayout="center" title={t('title')} desc={t('desc')} />

            <div className="partner-form-container">
                <Form name="partner-form" form={form} layout="vertical" onFinish={onFinish}>
                    <div className="two-colums">
                        <Form.Item
                            label={t('first_name_label')}
                            name="firstName"
                            required
                            rules={[
                                {
                                    validator: async (_: any, value: any) => {
                                        if (!value || value?.length > 50) {
                                            return Promise.reject(t2('presale.preorder.firstname_invalid'));
                                        }
                                        return Promise.resolve();
                                    },
                                },
                            ]}
                        >
                            <Input className="default-input" placeholder={t('first_name_label')} aria-label={t('first_name_label')} />
                        </Form.Item>
                        <Form.Item
                            label={t('last_name_label')}
                            name="lastName"
                            rules={[
                                {
                                    validator: async (_: any, value: any) => {
                                        if (!value || value?.length > 50) {
                                            return Promise.reject(t2('presale.preorder.lastname_invalid'));
                                        }
                                        return Promise.resolve();
                                    },
                                },
                            ]}
                        >
                            <Input className="default-input" placeholder={t('last_name_label')} aria-label={t('last_name_label')} />
                        </Form.Item>
                    </div>
                    <div className="two-colums">
                        <Form.Item
                            label={t('email_label')}
                            name="email"
                            required
                            rules={[
                                {
                                    validator: async (_: any, value: any) => {
                                        const regx = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;
                                        if (!regx.test(value) || value?.length > 100) {
                                            return Promise.reject(t2('presale.preorder.email_error_tips'));
                                        }
                                        return Promise.resolve();
                                    },
                                },
                            ]}
                        >
                            <Input className="default-input" placeholder={t('email_label')} aria-label={t('email_label')} />
                        </Form.Item>
                        <Form.Item
                            label={t('zip_code_label')}
                            name="zip"
                            required
                            rules={[
                                {
                                    validator(_: any, value: any) {
                                        const regx = /^[a-zA-Z0-9\s]{1,10}$/;
                                        if (!value || !regx.test(value)) {
                                            return Promise.reject(t2('user.site_account_address_zip_wrong_tip'));
                                        }
                                        return Promise.resolve();
                                    },
                                },
                            ]}
                        >
                            <Input className="default-input" placeholder={t('zip_code_label')} aria-label={t('zip_code_label')} />
                        </Form.Item>
                    </div>
                    <div className="two-colums">
                        <Form.Item
                            label={t('country_label')}
                            name="countryName"
                            required
                            rules={[
                                {
                                    validator: (_rule, value) =>
                                        value ? Promise.resolve() : Promise.reject(t2('user.site_account_profile_country_select_wrong_tip')),
                                },
                            ]}
                        >
                            <Select
                                optionFilterProp="children"
                                placeholder={t('country_label')}
                                getPopupContainer={(triggerNode: any) => triggerNode.parentNode || document.body}
                                showSearch
                                onChange={onCountryChange}
                            >
                                {countries?.map((item: any) => (
                                    <Select.Option value={item.text} key={item.key}>
                                        {item.text}
                                    </Select.Option>
                                ))}
                            </Select>
                        </Form.Item>
                        <Form.Item
                            label={t('state_label')}
                            name="state"
                            required
                            rules={[
                                {
                                    validator: (_rule, value) => (value ? Promise.resolve() : Promise.reject(t2('presale.preorder.state_empty'))),
                                },
                            ]}
                        >
                            <Select
                                optionFilterProp="children"
                                placeholder={t('state_label')}
                                getPopupContainer={(triggerNode: any) => triggerNode.parentNode || document.body}
                                showSearch
                                onChange={onStateChange}
                                options={states}
                            />
                        </Form.Item>
                    </div>
                    <div className="two-colums">
                        <Form.Item
                            label={t('county_label')}
                            name="county"
                            required
                            rules={[
                                {
                                    validator: (_rule, value) => (value ? Promise.resolve() : Promise.reject(t2('presale.preorder.city_empty'))),
                                },
                            ]}
                        >
                            <Select
                                optionFilterProp="children"
                                placeholder={t('county_label')}
                                getPopupContainer={(triggerNode: any) => triggerNode.parentNode || document.body}
                                showSearch
                                options={cities}
                            />
                        </Form.Item>
                        <Form.Item
                            label={t('phone_label')}
                            name="phone"
                            required
                            rules={[
                                {
                                    validator(_: any, value: any) {
                                        const regx = /^[0-9+]{7,20}$/;
                                        if (!regx.test(value)) {
                                            return Promise.reject(t2('my.site_account_address_phone_wrong_tip'));
                                        }
                                        return Promise.resolve();
                                    },
                                },
                            ]}
                        >
                            <Input className="default-input" placeholder={t('phone_label')} aria-label={t('phone_label')} />
                        </Form.Item>
                    </div>

                    <div className="two-colums">
                        <Form.Item
                            name="recaptchaResponse"
                            rules={[
                                {
                                    validator() {
                                        if (recaptchaResponse) {
                                            return Promise.resolve();
                                        }
                                        return Promise.reject(t2('logintips.site_login_tip_perform_verification'));
                                    },
                                },
                            ]}
                        >
                            <div className="g-recaptcha">
                                {/* @ts-ignore */}
                                <ReCAPTCHA ref={recaptchaRef} hl={hl} sitekey={SECRET_KEY} onChange={onReCAPTCHAChange} />
                            </div>
                        </Form.Item>
                    </div>
                    <div className="two-colums">
                        <Form.Item
                            name="agreement"
                            valuePropName="checked"
                            rules={[
                                {
                                    validator: (_, value) => (value ? Promise.resolve() : Promise.reject(new Error(t2('logintips.site_check_agreement_text')))),
                                },
                            ]}
                        >
                            <Checkbox>
                                <span>
                                    {t.rich('check_policy', {
                                        link1: (children: any) => (
                                            <Link
                                                href={
                                                    locale === 'cis'
                                                        ? 'https://account.ecoflow.com/agreement/ru-cis/PrivacyPolicy.html'
                                                        : locale === 'uk' && kaamelInfo?.privacyUrl
                                                        ? kaamelInfo?.privacyUrl
                                                        : `https://account.ecoflow.com/agreement/${i18nMap[locale as string] || i18nMap.us}/PrivacyPolicy.html`
                                                }
                                                target="_blank"
                                                aria-label={t('privacy')}
                                                className="privacy"
                                            >
                                                {children}
                                            </Link>
                                        ),
                                    })}
                                </span>
                            </Checkbox>
                        </Form.Item>
                    </div>

                    <div className="two-colums">
                        <div className="submit-container">
                            <Form.Item>
                                <Button
                                    type="primary"
                                    htmlType="submit"
                                    className="submit-btn GtmModuleData-btn-text"
                                    loading={loading}
                                    aria-label={t('submit_btn')}
                                >
                                    {t('submit_btn')}
                                </Button>

                                <p className="note">
                                    <input
                                        type="checkbox"
                                        id="agreement"
                                        className="agreement-checkbox"
                                        checked={isAgreed}
                                        onChange={(e) => setIsAgreed(e.target.checked)}
                                    />
                                    {t(`note`)}
                                </p>
                            </Form.Item>
                        </div>
                    </div>
                </Form>
            </div>
        </div>
    );
}
